import { ModalTestIds } from '@/enums/modalTestIds.enum';
import { useToast } from '@/hooks/useToast';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent } from '@mui/material';
import { AxiosError } from 'axios';
import { ReactNode, useState } from 'react';
import { TitleText } from '../TitleText';
import { BodyText } from '../BodyText';

type MutateFunction<P> = ((parameters: P) => void) | ((parameters?: P) => void);

export const DeleteModal = <T, P = void, H = object>({
  useHook,
  mutateParams,
  hookParams,
  isOpen,
  onClose,
  title,
  itemName,
  item,
  confirmButtonText = 'Delete',
  confirmButtonLoadingText = 'Deleting...',
  cancelButtonText = 'Cancel',
  verb = 'delete',
  renderItemName,
  onConfirm,
  isLoading,
  alertSeverity,
  alertMessage,
  children,
}: React.PropsWithChildren<{
  useHook: (parameters: H & { onSuccess?: () => void; onError?: (error: string) => void }) => {
    mutate: MutateFunction<P>;
    isPending: boolean;
    error?: string | AxiosError<unknown> | null;
  };
  mutateParams?: P;
  hookParams?: H;
  isOpen: boolean;
  onClose: () => void;
  title: string;
  itemName: string;
  item: T;
  confirmButtonText?: string;
  confirmButtonLoadingText?: string;
  cancelButtonText?: string;
  verb?: string;
  renderItemName?: (item: T) => ReactNode;
  onConfirm?: () => void;
  isLoading?: boolean;
  alertSeverity?: 'warning' | 'info' | 'error' | 'success';
  alertMessage?: ReactNode;
  children?: ReactNode;
}>) => {
  const { showToast } = useToast();
  const [error, setError] = useState<string>();
  const customItemName = renderItemName && item ? renderItemName(item) : itemName;

  const { mutate, isPending } = useHook({
    onSuccess: () => {
      showToast({
        message: `Deleted "${typeof customItemName === 'string' ? customItemName : itemName}"`,
        type: 'success',
      });
      onClose();
    },
    onError: setError,
    ...((hookParams || {}) as H),
  });

  const handleConfirm = () => {
    if (mutateParams === undefined) {
      (mutate as () => void)();
    } else {
      (mutate as (parameters: P) => void)(mutateParams);
    }
    onConfirm?.();
  };

  return (
    <Dialog
      fullWidth
      open={isOpen}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
          },
        },
      }}
    >
      <TitleText sx={{ padding: '24px 8px 0 24px' }}>{title}</TitleText>
      <DialogContent sx={{ padding: '8px 24px 24px 24px' }}>
        {alertSeverity && alertMessage && (
          <Alert severity={alertSeverity} sx={{ marginBottom: '18px' }}>
            {alertMessage}
          </Alert>
        )}
        {typeof customItemName === 'object' ? (
          customItemName
        ) : (
          <BodyText color='text.primary'>
            Are you sure you want to {verb} {customItemName}? This action cannot be undone.
          </BodyText>
        )}
        {children && <Box sx={{ paddingTop: '18px' }}>{children}</Box>}
        {error && <Alert severity='error'>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ padding: '0 24px 24px 24px' }}>
        <Button
          onClick={onClose}
          disabled={isPending}
          sx={{ textTransform: 'none', borderRadius: '12px', padding: '12px 16px' }}
        >
          <BodyText color='text.primary'>{cancelButtonText}</BodyText>
        </Button>
        <Button
          variant='contained'
          color='error'
          onClick={handleConfirm}
          disabled={isPending || isLoading}
          data-testid={ModalTestIds.DeleteModalConfirmButton}
          sx={{ textTransform: 'none', borderRadius: '12px', padding: '8px 16px' }}
        >
          {isPending ? confirmButtonLoadingText : confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
